import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      // Verify the session and fetch subscription details
      verifySession();
    } else {
      toast.error('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const verifySession = async (retryCount = 0) => {
    try {
      // Verify the checkout session and create subscription if needed
      const response = await fetch(`/api/subscriptions/verify-checkout?session_id=${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.subscription) {
          toast.success('Subscription activated successfully!');
          setLoading(false);
          return;
        }
      } else {
        const errorData = await response.json();
        console.error('Error verifying checkout:', errorData);
        
        // If it's a metadata error, show a helpful message
        if (errorData.error && errorData.error.includes('plan_id missing')) {
          toast.error('Subscription created but missing plan information. Please contact support.');
          setLoading(false);
          return;
        }
      }

      // Retry up to 3 times with increasing delays
      if (retryCount < 3) {
        setTimeout(() => {
          verifySession(retryCount + 1);
        }, 2000 * (retryCount + 1)); // 2s, 4s, 6s
      } else {
        toast.error('Unable to verify subscription. Please check your subscription page.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error verifying subscription:', error);
      if (retryCount < 3) {
        setTimeout(() => {
          verifySession(retryCount + 1);
        }, 2000 * (retryCount + 1));
      } else {
        toast.error('Failed to verify subscription. Please try again later.');
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing your subscription...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        ) : (
          <>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Subscription Successful!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for subscribing! Your subscription is now active and you have full access to all features.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/subscription')}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                View Subscription
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
